import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Upload, Image, Video, Trash2, Eye, EyeOff } from "lucide-react";

function mediaUrl(u) {
  if (!u) return u;
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

export default function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/gallery");
      setItems(r.data);
    } catch {
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("da_admin_token");
      const res = await fetch(`${API}/admin/upload-media`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Upload failed");
      }
      const data = await res.json();
      await api.post("/admin/gallery", {
        type: data.type,
        url: data.url,
        caption: "",
      });
      toast.success("Media added to gallery");
      load();
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const toggleActive = async (it) => {
    try {
      await api.patch(`/admin/gallery/${it.id}`, { active: !it.active });
      load();
    } catch {
      toast.error("Update failed");
    }
  };
  const setCaption = async (it, caption) => {
    if (caption === (it.caption || "")) return;
    try {
      await api.patch(`/admin/gallery/${it.id}`, { caption });
    } catch {
      toast.error("Update failed");
    }
  };
  const setOrder = async (it, order) => {
    const n = parseInt(order, 10);
    if (isNaN(n) || n === it.order) return;
    try {
      await api.patch(`/admin/gallery/${it.id}`, { order: n });
      load();
    } catch {
      toast.error("Update failed");
    }
  };
  const del = async (it) => {
    if (!window.confirm("Delete this item from gallery?")) return;
    try {
      await api.delete(`/admin/gallery/${it.id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="gallery-panel">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div>
          <p className="text-sm text-[#6E675F]">Upload photos & short videos to showcase on the homepage.</p>
          <p className="text-xs text-[#6E675F]">Photos up to 6MB · Videos up to 40MB (mp4/webm)</p>
        </div>
        <label className="cursor-pointer">
          <span className={`inline-flex items-center rounded-full px-5 py-2.5 btn-primary text-sm font-medium ${uploading ? "opacity-60" : ""}`}>
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? "Uploading…" : "Upload media"}
          </span>
          <input
            data-testid="gallery-upload-input"
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            disabled={uploading}
          />
        </label>
      </div>

      {loading ? (
        <div className="py-8 text-center text-[#6E675F]">Loading gallery...</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E6DEC3] rounded-2xl p-12 text-center">
          <Image className="w-12 h-12 text-[#D9822B] mx-auto mb-3" />
          <p className="font-serif text-lg">Gallery is empty</p>
          <p className="text-sm text-[#6E675F] mt-1">Upload your first photo or video to bring the homepage alive.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} data-testid={`gallery-admin-${it.id}`} className={`bg-white border rounded-2xl overflow-hidden ${it.active ? "border-[#E6DEC3]" : "border-[#D97706] opacity-70"}`}>
              <div className="aspect-video bg-[#F4EFE6] relative">
                {it.type === "video" ? (
                  <video src={mediaUrl(it.url)} className="w-full h-full object-cover" controls />
                ) : (
                  <img src={mediaUrl(it.url)} alt={it.caption || ""} className="w-full h-full object-cover" />
                )}
                <span className="absolute top-2 left-2 bg-white/95 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                  {it.type === "video" ? <><Video className="w-3 h-3" /> Video</> : <><Image className="w-3 h-3" /> Photo</>}
                </span>
              </div>
              <div className="p-3 space-y-2">
                <Input
                  data-testid={`gallery-caption-${it.id}`}
                  defaultValue={it.caption || ""}
                  onBlur={(e) => setCaption(it, e.target.value)}
                  placeholder="Caption (optional)"
                  className="h-8 text-xs"
                />
                <div className="flex items-center gap-2">
                  <Input
                    data-testid={`gallery-order-${it.id}`}
                    type="number"
                    defaultValue={it.order}
                    onBlur={(e) => setOrder(it, e.target.value)}
                    className="h-8 w-20 text-xs"
                  />
                  <span className="text-xs text-[#6E675F]">order</span>
                  <div className="flex-1" />
                  <button
                    onClick={() => toggleActive(it)}
                    className="text-xs text-[#6E675F] hover:text-[#9E3B21] flex items-center gap-1"
                    data-testid={`gallery-toggle-${it.id}`}
                  >
                    {it.active ? <><Eye className="w-3.5 h-3.5" /> Shown</> : <><EyeOff className="w-3.5 h-3.5" /> Hidden</>}
                  </button>
                  <button
                    onClick={() => del(it)}
                    className="text-xs text-[#9E3B21] hover:underline flex items-center gap-1"
                    data-testid={`gallery-delete-${it.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
